<template>
  <SheetModal
    class="EditSeasonRosterModalView"
    data-theme="light"
    aria-label="Manage the roster"
    :aria-busy="isRequesting || isSaving"
    @close="handleClose"
  >
    <form
      class="EditSeasonRosterModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="EditSeasonRosterModalView__fieldset"
        :disabled="isRequesting || isSaving"
      >
        <SheetHeader
          title="Manage the roster"
          @cancel="handleClose"
        />
        <Transition>
          <div
            v-if="!isRequesting"
            class="EditSeasonRosterModalView__content"
          >
            <PlayerList
              ref="regularPlayersListRef"
              class="EditSeasonRosterModalView__playerList"
              title="Regulars"
              subtitle="Can update their status"
              :players="regularPlayers"
              isActive
            >
              <template #buttonList="{ player }">
                <IconButton
                  variant="secondary"
                  shape="circle"
                  size="smedium"
                  iconSize="smedium"
                  iconName="minus"
                  :aria-label="`Remove ${player.name} from the roster`"
                  @click="handleRemovePlayerClick(player)"
                />
              </template>
              <template #emptyState>
                <EmptyState
                  data-testid="regularsEmptyState"
                  iconName="peoplePlus"
                  iconSize="xxlarge"
                >
                  <h3>You don’t have any regulars yet</h3>
                  <p>
                    Add players from the list below
                  </p>
                </EmptyState>
              </template>
            </PlayerList>
            <PlayerList
              class="EditSeasonRosterModalView__playerList"
              title="Subs"
              subtitle="Must join the waitlist & be approved"
              :players="subPlayers"
            >
              <template #buttonList="{ player }">
                <IconButton
                  shape="circle"
                  size="smedium"
                  iconSize="smedium"
                  iconName="plus"
                  :aria-label="`Add ${player.name} to the roster`"
                  @click="handleAddPlayerClick(player)"
                />
              </template>
              <template #emptyState>
                <EmptyState
                  data-testid="subsEmptyState"
                  iconName="peoplePlus"
                  iconSize="xxlarge"
                >
                  <h3>You don’t have any <span v-if="regularPlayers.length > 0">more</span> subs</h3>
                  <p>
                    Invite more players to your crew
                  </p>
                  <FormButton
                    size="small"
                    @click="handleInvitePlayerClick"
                  >
                    Invite a player
                  </FormButton>
                </EmptyState>
              </template>
            </PlayerList>
          </div>
        </Transition>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script setup lang="ts">
import EmptyState from "@/components/EmptyState.vue";
import FormButton from "@/components/FormButton.vue";
import IconButton from "@/components/IconButton.vue";
import PlayerList from "@/components/PlayerList.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import { supabaseClient } from "@/lib/database/supabase";
import { RouteName } from "@/lib/router/routes";
import isTruthy from "@/lib/utils/boolean/isTruthy";
import haptic from "@/lib/utils/platform/haptic";
import useModalStore from "@/stores/useModalStore";
import useRunCarouselStore from "@/stores/useRunCarouselStore";
import useSeasonStore from "@/stores/useSeasonStore";
import { PlayerSummary } from "@shared/lib/database/query.types";
import { ComponentPublicInstance, computed, nextTick, onBeforeMount, ref, useTemplateRef } from "vue";

const {
  seasonId,
} = defineProps<{
  seasonId: string,
}>();
const isRequesting = ref(false);
const isSaving = ref(false);

const {
  openModal,
  closeActiveModal,
} = useModalStore();

const {
  fetchSeason,
  updateSeasonRegulars,
} = useSeasonStore();

const {
  fetchCurrentRuns,
} = useRunCarouselStore();

const regularPlayerIds = ref<Array<string>>([]);
const playerMap = ref<Map<string, PlayerSummary>>(new Map());
const crewPlayers = ref<Array<PlayerSummary>>([]);
const regularPlayersListRef = useTemplateRef<ComponentPublicInstance<typeof PlayerList>>("regularPlayersListRef");

onBeforeMount(async () => {
  isRequesting.value = true;

  try {
    const season = await fetchSeason(seasonId);

    if (season) {
      const regularPlayers = sortPlayersByName(season.season_regulars.map((seasonRegular) => seasonRegular.player) || []);
      crewPlayers.value = sortPlayersByName(season.crew.players || []);
      playerMap.value = new Map([...regularPlayers, ...crewPlayers.value].map((player) => [player.id, player]));
      regularPlayerIds.value = regularPlayers.map((player) => player.id);
    }
  } catch (error) {
    alert((error as Error).message);
  }

  isRequesting.value = false;
});

const regularPlayers = computed(() => {
  return regularPlayerIds.value.map((id) => playerMap.value.get(id)).filter(isTruthy);
});

const subPlayers = computed(() => {
  return crewPlayers.value.filter((player) => !regularPlayerIds.value.includes(player.id));
});

function sortPlayersByName (players: Array<PlayerSummary>) {
  return players.sort((a, b) => a.name.localeCompare(b.name));
}

function handleAddPlayerClick (player: PlayerSummary) {
  haptic();
  regularPlayerIds.value.push(player.id);
  nextTick().then(() => regularPlayersListRef.value?.scrollToBottom());
}

function handleRemovePlayerClick (player: PlayerSummary) {
  haptic();
  regularPlayerIds.value = regularPlayerIds.value.filter((id) => id !== player.id);
}

function handleInvitePlayerClick () {
  haptic();
  openModal({
    name: RouteName.invitePlayer,
  });
}

function handleClose () {
  closeActiveModal();
}

async function handleSubmit () {
  isSaving.value = true;

  const {
    data: players,
    error,
  } = await supabaseClient.rpc("update_season_regulars", {
    season_id: seasonId,
    player_ids: regularPlayerIds.value,
  });

  isSaving.value = false;

  if (players) {
    updateSeasonRegulars(
      seasonId,
      players,
    );
    fetchCurrentRuns(); // TODO improve this is a hack to update the runs list
    closeActiveModal();
  } else if (error) {
    alert(error.message);
  }
}
</script>

<style lang="scss" scoped>
.EditSeasonRosterModalView__form {
  position: absolute;
  inset: 0;
}

.EditSeasonRosterModalView__fieldset {
  display: grid;
  grid-template-rows: min-content auto;
  height: 100%;
}

.EditSeasonRosterModalView__content {
  display: grid;
  grid-auto-rows: 1fr;
  gap: 16px;
  min-height: 0;
  padding: 20px 16px calc(20px + var(--safeAreaInsetBottom));

  transition: opacity 350ms;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.EditSeasonRosterModalView__playerList {
  min-height: 0;
}
</style>
